import React from 'react';
import s from './s.module.less';
import { Input, InputRef, message } from 'antd';
import { GOOGLE_MAPS_API_KEY } from 'constants/common';
import { ServerMeta } from 'types/common';

// 仅在本文件内定义 loadGoogleMapsScript
let googleMapsScriptLoading: Promise<void> | null = null;
function loadGoogleMapsScript(apiKey: string) {
    if (typeof window !== 'undefined' && window.google && window.google.maps) {
        return Promise.resolve();
    }
    if (!googleMapsScriptLoading) {
        googleMapsScriptLoading = new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly`;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            document.body.appendChild(script);
        });
    }
    return googleMapsScriptLoading;
}
declare global {
    interface Window {
        google: any;
    }
}

export type AddressDisplayField = 'street' | 'suite' | 'city' | 'state' | 'postcode';

type Props = React.ComponentProps<typeof Input> & {
    value?: string;
    onChange?: (val: string) => void;
    wrapClass?: string;
    size?: 'large' | 'small' | 'middle';
    onCityChange?: (val: string) => void;
    onStateChange?: (val: string) => void;
    onPostcodeChange?: (val: string) => void;
    useFullAddress?: boolean;
    addCityToAddress?: boolean;
    // 选中后输入框回填哪些字段，优先级高于 useFullAddress / addCityToAddress
    displayFields?: AddressDisplayField[];
    targetState?: ServerMeta;
};

const AutoAddressInput = (props: Props) => {
    const {
        value,
        onChange,
        addCityToAddress,
        size = 'middle',
        onCityChange,
        onStateChange,
        useFullAddress,
        onPostcodeChange,
        displayFields,
        targetState,
        ...others
    } = props;
    const [val, setVal] = React.useState(value || '');
    const inputRef = React.useRef<InputRef | null>(null);
    const autocomplete = React.useRef<any>();

    React.useEffect(() => {
        if (value !== undefined) {
            setVal(value);
        }
    }, [value]);

    React.useEffect(() => {
        if (val === '') {
            return;
        }
        onChange && onChange(val);
    }, [val]);

    React.useEffect(() => {
        let autocompleteInstance: any;
        loadGoogleMapsScript(GOOGLE_MAPS_API_KEY).then(() => {
            if (inputRef.current && inputRef.current.input && window.google) {
                autocompleteInstance = new window.google.maps.places.Autocomplete(inputRef.current.input, {
                    componentRestrictions: { country: ['us'] },
                    fields: ['address_components', 'geometry'],
                    types: ['address'],
                });
                autocomplete.current = autocompleteInstance;
                autocompleteInstance.addListener('place_changed', () => {
                    const place = autocompleteInstance.getPlace();
                    let _address = '';
                    let _suite = '';
                    let _city = '';
                    let _state = '';
                    let _postcode = '';
                    // locality 缺失时的 city 兜底（如 Cordova, TN 这类非建制社区只有 neighborhood）
                    const cityFallbacks: Record<string, string> = {};

                    // Check if address_components exists and is iterable
                    if (!place.address_components || !Array.isArray(place.address_components)) {
                        return;
                    }

                    if (targetState) {
                        const isTargetAddress = place.address_components?.some(
                            (comp: any) =>
                                comp.types.includes('administrative_area_level_1') &&
                                comp.short_name === targetState?.dictKey,
                        );
                        if (!isTargetAddress) {
                            message.error(`Please input the address in ${targetState?.content}`);
                            setVal('');
                            return;
                        }
                    }
                    for (const component of place.address_components) {
                        // @ts-ignore remove once typings fixed
                        const componentType = component.types[0];
                        switch (componentType) {
                            case 'street_number': {
                                _address = `${component.long_name} ${_address}`;
                                break;
                            }
                            case 'route': {
                                _address += component.short_name;
                                break;
                            }
                            case 'subpremise': {
                                _suite = component.long_name;
                                break;
                            }
                            case 'postal_code': {
                                _postcode = component.long_name;
                                break;
                            }
                            case 'postal_code_suffix': {
                                _postcode = `${_postcode}-${component.long_name}`;
                                break;
                            }
                            case 'locality': {
                                _city = component.long_name;
                                break;
                            }
                            case 'sublocality_level_1':
                            case 'neighborhood':
                            case 'administrative_area_level_3':
                            case 'postal_town': {
                                cityFallbacks[componentType] = component.long_name;
                                break;
                            }
                            case 'administrative_area_level_1': {
                                _state = component.short_name;
                                break;
                            }
                            case 'country': {
                                break;
                            }
                            default:
                                break;
                        }
                    }
                    if (!_city) {
                        _city =
                            cityFallbacks.sublocality_level_1 ||
                            cityFallbacks.neighborhood ||
                            cityFallbacks.administrative_area_level_3 ||
                            cityFallbacks.postal_town ||
                            '';
                    }
                    if (_address) {
                        if (displayFields && displayFields.length > 0) {
                            const fieldValues: Record<AddressDisplayField, string> = {
                                street: _address,
                                suite: _suite,
                                city: _city,
                                state: _state,
                                postcode: _postcode,
                            };
                            _address = displayFields
                                .map((field) => fieldValues[field])
                                .filter(Boolean)
                                .join(', ');
                        } else if (useFullAddress) {
                            _address = `${_address}, ${_city}, ${_state}, ${_postcode}`;
                        } else if (addCityToAddress && _city) {
                            _address = `${_address}, ${_city}`;
                        }
                        onChange && onChange(_address);
                        setVal(_address);
                    }
                    if (_city) {
                        onCityChange && onCityChange(_city);
                    }
                    if (_state) {
                        onStateChange && onStateChange(_state);
                    }
                    if (_postcode) {
                        onPostcodeChange && onPostcodeChange(_postcode);
                    }
                });
            }
        });
        return () => {
            if (autocompleteInstance && window.google) {
                window.google.maps.event.clearInstanceListeners(autocompleteInstance);
            }
        };
    }, []);

    return (
        <div className={s.wrap}>
            <Input
                size={size}
                {...others}
                placeholder={others.placeholder || ''}
                className={`${s.input} ${others.className} ${s.addressInput}`}
                maxLength={35}
                ref={inputRef}
                value={val}
                onChange={(e) => {
                    setVal(e.target.value);
                }}
            />
        </div>
    );
};

export default AutoAddressInput;
